import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

// ANTD
import {
	Upload as AntUpload,
	Button as AntButton,
	message as AntMessage,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';

// REDUX
import { ApplicationState } from 'store';
import { clearNotifications } from 'store/modules/app/notifications/actions';

// COMPONENTS & UTILS
import Section from 'components/Common/Section';
import { AuthContext, IAuthContext } from 'react-oauth2-code-pkce';
import {
	cadastraDocumentosRequest,
	limpaCadastraDocumentos,
} from 'store/modules/api/iirgd/cadastraDocumentos/actions';
import { treatPathBaseIntegradorUrl } from 'services/rotasp/utils/paths/treatPaths';
import { PATH_ARQUIVOS_API } from 'services/rotasp/path';
import { letrasRemoveApostrofoAcentoGraveAgudoDuploEspacoCedilhaAcentos } from 'utils/stringFunctions';
import { ConsultarCpfCin } from 'store/modules/api/iirgd/integrador/consultarCin/types';
import { limparMascara } from 'utils/genericFunctions';
import { IFormCadastroRg } from '../../form';

interface DivergenceFormProps {
	initialData: IFormCadastroRg;
	consultarCinData: ConsultarCpfCin;
	onUploadComplete: (success: boolean) => void;
}

const DivergenceForm: React.FC<DivergenceFormProps> = ({
	initialData,
	consultarCinData,
	onUploadComplete,
}) => {
	const rfbDataDivergences =
		(initialData.dadosRfb &&
			(!initialData.dadosRfb.resultValidations.nameValidation ||
				!initialData.dadosRfb.resultValidations.motherNameValidation ||
				!initialData.dadosRfb.resultValidations.birthDayValidation)) ||
		false;

	const resultCPFDivergence =
		!!consultarCinData &&
		consultarCinData.codSitCad !== '0' &&
		consultarCinData.codSitCad !== '4';

	const dispatch = useDispatch();
	const { token } = useContext<IAuthContext>(AuthContext);
	const [isUploading, setIsUploading] = useState(false);
	const [uploadError, setUploadError] = useState<string | null>(null);
	const [fileList, setFileList] = useState<any[]>([]);
	const [uploadedFileUrl, setUploadedFileUrl] = useState<string>('');

	const { cadastraDocumentos } = useSelector(
		(state: ApplicationState) => state.api.iirgd,
	);

	useEffect(() => {
		if (cadastraDocumentos.status === 400) {
			const errorMsg = 'Erro ao fazer upload do arquivo.';
			setUploadError(errorMsg);
			AntMessage.error(errorMsg);
		} else if (cadastraDocumentos.status === 201) {
			AntMessage.success(
				'Documento de divergência registrado com sucesso!',
				undefined,
				() => {
					onUploadComplete(true);
				},
			);
		}
	}, [cadastraDocumentos.status, onUploadComplete]);

	const handleClearUploadedFiles = useCallback(() => {
		setFileList([]);
		setUploadedFileUrl('');
		onUploadComplete(false);
		setUploadError(null);
		dispatch(limpaCadastraDocumentos());
		dispatch(clearNotifications());
	}, [onUploadComplete, dispatch]);

	const handleSubmitDocument = (url: string) => {
		const payload = {
			cpf: limparMascara(initialData.cpf) || '',
			rg: limparMascara(initialData.numeroRg) || '',
			urlArquivo: url,
		};
		dispatch(cadastraDocumentosRequest(token, payload));
	};

	const antDUploadProps = {
		name: 'file',
		multiple: false,
		accept: '.pdf,.png,.jpg,.jpeg',
		fileList,
		customRequest: async ({ file, onSuccess, onError }: any) => {
			handleClearUploadedFiles();
			setIsUploading(true);
			const rcFile = file as File;

			if (rcFile.size > 1500000) {
				// 1.5MB
				const errorMsg = 'Tamanho máximo permitido é de 1.5MB.';
				setUploadError(errorMsg);
				AntMessage.error(errorMsg);
				setIsUploading(false);
				if (onError) onError(new Error(errorMsg));
				return;
			}

			const uploadUrlTarget = `${treatPathBaseIntegradorUrl(
				PATH_ARQUIVOS_API,
			)}/ds/${uuidv4()}`;

			try {
				const response = await axios.post(uploadUrlTarget, rcFile, {
					headers: {
						'Content-Type': 'application/octet-stream',
						Authorization: `Bearer ${token}`,
					},
				});
				if (response.data && response.data.url) {
					setUploadedFileUrl(response.data.url);
					setFileList([{ uid: '-1', name: rcFile.name, status: 'done' }]);
					AntMessage.success(`${rcFile.name} enviado com sucesso.`);
					if (onSuccess) onSuccess(response.data, file);
					handleSubmitDocument(response.data.url);
				} else {
					throw new Error('URL não retornada pelo servidor.');
				}
			} catch (err) {
				const errorMsg = 'Erro ao fazer upload do arquivo.';
				setUploadError(errorMsg);
				AntMessage.error(errorMsg);
				if (onError) onError(err);
			} finally {
				setIsUploading(false);
			}
		},
		onRemove: () => {
			handleClearUploadedFiles();
			return true;
		},
	};

	const renderDataRFB = () => {
		const nomeMsg = initialData.dadosRfb?.resultValidations.nameValidation ? (
			<>
				<strong>Nome cidadão:</strong> OK
			</>
		) : (
			<>
				<strong>Nome cidadão:</strong> NOK <br /> <strong>RFB:</strong>{' '}
				{letrasRemoveApostrofoAcentoGraveAgudoDuploEspacoCedilhaAcentos(
					consultarCinData?.nomeContribuinte || '',
				)
					.toUpperCase()
					.trim()}{' '}
				<br /> <strong>BU:</strong>{' '}
				{letrasRemoveApostrofoAcentoGraveAgudoDuploEspacoCedilhaAcentos(
					initialData?.nome || '',
				)
					.toUpperCase()
					.trim()}
			</>
		);

		const maeMsg = initialData.dadosRfb?.resultValidations
			.motherNameValidation ? (
				<>
				<strong>Filiação 2:</strong> OK
			</>
		) : (
			<>
				<strong>Filiação 2:</strong> NOK <br /> <strong>RFB:</strong>{' '}
				{letrasRemoveApostrofoAcentoGraveAgudoDuploEspacoCedilhaAcentos(
					consultarCinData?.nomeMae || '',
				)
					.toUpperCase()
					.trim()}
				<br /> <strong>BU:</strong>{' '}
				{letrasRemoveApostrofoAcentoGraveAgudoDuploEspacoCedilhaAcentos(
					initialData.nomeMae || '',
				)
					.toUpperCase()
					.trim()}
			</>
		);

		const nascimentoMsg = initialData.dadosRfb?.resultValidations
			.birthDayValidation ? (
				<>
				<strong>Data nascimento:</strong> OK
			</>
		) : (
			<>
				<strong>Data nascimento:</strong> NOK <br /> <strong>RFB:</strong>{' '}
				{consultarCinData?.dtNasc.substring(6, 8) || ''}/
				{consultarCinData?.dtNasc.substring(4, 6) || ''}/
				{consultarCinData?.dtNasc.substring(0, 4) || ''} <br />
				<strong>BU:</strong> {initialData?.dataNascimento.trim()}
			</>
		);

		return (
			<>
				<p style={{ marginTop: '5px' }}>{nomeMsg}</p>
				<p style={{ marginTop: '5px' }}>{maeMsg}</p>
				<p style={{ marginTop: '5px' }}>{nascimentoMsg}</p>
			</>
		);
	};

	return (
		<Section title="Anexar Documento de Divergência (Certidão)">
			<p>Constatamos divergências com a base da Receita Federal.</p>
			{resultCPFDivergence && rfbDataDivergences && (
				<p>
					<strong>Situação do CPF:</strong> Diverge de regular e pendente de
					regularização, será analisada pela Receita Federal do Brasil.
				</p>
			)}
			{resultCPFDivergence && !rfbDataDivergences && (
				<>
					<p>
						<strong>Situação do CPF:</strong> Diverge de regular e pendente de
						regularização.
					</p>
					<p style={{ marginTop: '10px' }}>
						Inclua a Certidão para que a situação do CPF seja analisada pela
						Receita Federal.
					</p>
				</>
			)}
			{rfbDataDivergences && (
				<>
					<p style={{ marginTop: '5px' }}>{renderDataRFB()}</p>
					<p>
						<u style={{ marginTop: '10px' }}>
							Verifique se há erro de digitação para a divergência acima ou se
							está na RFB.
						</u>
					</p>
					<p style={{ marginTop: '5px' }}>
						Cadastro do BU correto, inclua a Certidão. As informações inseridas
						neste atendimento serão atualizadas na Receita Federal.
					</p>
				</>
			)}

			{((resultCPFDivergence && rfbDataDivergences) || resultCPFDivergence) && (
				<>
					<p style={{ marginTop: '5px' }}>
						Em casos dos documentos com informações no verso, solicitamos que
						sejam escaneados frente e verso, no mesmo arquivo.
					</p>
					<p style={{ marginTop: '5px', marginBottom: '15px' }}>
						Por favor inserir documento Certidão.
					</p>
				</>
			)}
			<div
				style={{
					border: '1px dashed #d9d9d9',
					padding: '20px',
					borderRadius: '4px',
					textAlign: 'center',
					marginTop: '16px',
				}}
			>
				<AntUpload {...antDUploadProps}>
					<AntButton
						icon={<UploadOutlined />}
						loading={isUploading}
						disabled={isUploading || !!uploadedFileUrl}
					>
						{isUploading ? 'Enviando...' : 'Selecionar Arquivo'}
					</AntButton>
				</AntUpload>
				<small style={{ display: 'block', marginTop: '8px' }}>
					(Formatos: PDF, PNG, JPG, JPEG - Tamanho Máx: 1.5MB)
				</small>
				{uploadError && (
					<div style={{ color: 'red', marginTop: '5px' }}>{uploadError}</div>
				)}
			</div>
		</Section>
	);
};

export default DivergenceForm;
